import React, { useEffect, useRef, useState } from "react";
import "./InvoiceSettings.scss";
import { useGlobalSettings } from "../../../context/GlobalSettingsContext";
import {
  getAllGlobalSettingsData,
  updateInvoiceSettings,
} from "../../../services/api/GlobalSettingsApi";
import FirebaseUploader from "../../../components/FireBaseUploader/FirebaseUploader";
import { useToast } from "../../../context/ToastContext";
import { useNavigate } from "react-router-dom";
import JusticeLoader from "../../../components/JusticeLoader/JusticeLoader";
import { uploadFileWithProgress } from "../../../utils/ftpUploadUtils";
import { useAuth } from "../../../context/AuthContext";
import { BiUpload } from "react-icons/bi";

const InvoiceSettings = () => {
  const { company } = useAuth()
  const [fetchingData, setFetchingData] = useState(true);
  const navigate = useNavigate();
  const { globalSettings, setGlobalSettings } = useGlobalSettings();

  const [headerFile, setHeaderFile] = useState(null);
  const [footerFile, setFooterFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [uploading, setUploading] = useState({
    header: false,
    footer: false,
    signature: false
  });
  const [uploadProgress, setUploadProgress] = useState({
    header: 0,
    footer: 0,
    signature: 0
  });

  const headerInputRef = useRef(null);
  const footerInputRef = useRef(null);
  const signatureInputRef = useRef(null);

  const [settings, setSettings] = useState({
    displayTermsAndConditions: false,
    displayHeaderFooter: false,
    gstIncluded: false,
    notifyAdmin: false,
    isSignatureDigitalGenerated: false,

    headerText: "",
    description: "",
    address: "",
    gstNo: "",
    panNo: "",
    cinNo: "",
    header: "",
    footer: "",
    bankDetails: "",
    digitalSignature: "",

    predefinedNo: { text: "" },
    recieptNo: { text: "" },
    services: [],
  });

  const [tcRules, setTcRules] = useState([""]);
  const [loading, setLoading] = useState(false);
  const [newService, setNewService] = useState({ name: "" });
  const { showToast } = useToast();

  const handleFileUpload = async (selectedFile, type) => {
    if (!selectedFile) return;

    setUploading(prev => ({ ...prev, [type]: true }));
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));

    try {
      const filePath = `/${company._id}/others`;

      await uploadFileWithProgress(
        selectedFile,
        filePath,
        // Progress callback
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [type]: progress }));
        },
        // Success callback
        (result) => {
          const fieldName = type === 'signature' ? 'digitalSignature' : type;

          setSettings(prev => ({
            ...prev,
            [fieldName]: result.imageUrl
          }));
          setUploading(prev => ({ ...prev, [type]: false }));
          showToast(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`, 'success');
        },
        // Error callback
        (error) => {
          console.error(`${type} upload failed:`, error);
          setUploading(prev => ({ ...prev, [type]: false }));
          setUploadProgress(prev => ({ ...prev, [type]: 0 }));
          showToast(error.message || `${type} upload failed`, 'error');
        }
      );

    } catch (error) {
      console.error(`${type} upload failed:`, error);
      setUploading(prev => ({ ...prev, [type]: false }));
      setUploadProgress(prev => ({ ...prev, [type]: 0 }));
      showToast(error.message || `${type} upload failed`, 'error');
    }
  };

  const handleFileChange = (e, type) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (type === 'header') setHeaderFile(selectedFile);
      else if (type === 'footer') setFooterFile(selectedFile);
      else if (type === 'signature') setSignatureFile(selectedFile);

      handleFileUpload(selectedFile, type);
    }
  };

  const handleUploadClick = (type) => {
    if (type === 'header') headerInputRef.current?.click();
    else if (type === 'footer') footerInputRef.current?.click();
    else if (type === 'signature') signatureInputRef.current?.click();
  };

  const handleRemoveFile = (type) => {
    // Fix: Map 'signature' type to 'digitalSignature' field
    const fieldName = type === 'signature' ? 'digitalSignature' : type;

    setSettings(prev => ({ ...prev, [fieldName]: "" }));

    if (type === 'header') {
      setHeaderFile(null);
      if (headerInputRef.current) headerInputRef.current.value = "";
    } else if (type === 'footer') {
      setFooterFile(null);
      if (footerInputRef.current) footerInputRef.current.value = "";
    } else if (type === 'signature') {
      setSignatureFile(null);
      if (signatureInputRef.current) signatureInputRef.current.value = "";
    }
  };

  const getDisplayUrl = (url) => {
    if (!url) return "";
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${url}`;
  };

  // File uploader component
  const FileUploader = ({ label, type, existingUrl }) => (
    <div className="file-uploader">
      <label className="uploader-label">{label}</label>
      
      <input
        ref={type === 'header' ? headerInputRef : type === 'footer' ? footerInputRef : signatureInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileChange(e, type)}
        style={{ display: 'none' }}
        disabled={uploading[type]}
      />

      {!existingUrl && !uploading[type] && (
        <div 
          className="upload-placeholder" 
          onClick={() => handleUploadClick(type)}
        >
          <BiUpload size={24} color="#0066cc" />
          <p>Click to upload {label.toLowerCase()}</p>
        </div>
      )}

      {uploading[type] && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress[type]}%` }}
            />
          </div>
          <p>{uploadProgress[type]}% uploaded</p>
        </div>
      )}

      {existingUrl && !uploading[type] && (
        <div className="upload-success">
          <div className="file-info">
            {/* <div className="file-icon">📄</div> */}
            <span className="file-name">{label} uploaded successfully</span>
          </div>
          <div className="file-actions">
            <button 
              type="button" 
              onClick={() => window.open(getDisplayUrl(existingUrl), '_blank')}
              className="view-btn"
            >
              View
            </button>
            <button 
              type="button" 
              onClick={() => handleUploadClick(type)}
              className="change-btn"
            >
              Change
            </button>
            <button 
              type="button" 
              onClick={() => handleRemoveFile(type)}
              className="remove-btn"
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      try {
        setFetchingData(true);
        const response = await getAllGlobalSettingsData();

        if (response?.data?.result?.[0]?.invoiceSettings) {
          const invoiceSettings = response.data.result[0].invoiceSettings;

          // Set the settings with proper defaults
          setSettings({
            ...invoiceSettings,
            displayTermsAndConditions: invoiceSettings.displayTermsAndConditions ?? false,
            displayHeaderFooter: invoiceSettings.displayHeaderFooter ?? false,
            gstIncluded: invoiceSettings.gstIncluded ?? false,
            notifyAdmin: invoiceSettings.notifyAdmin ?? false,
            isSignatureDigitalGenerated: invoiceSettings.isSignatureDigitalGenerated ?? false,
            predefinedNo: invoiceSettings.predefinedNo ?? { text: "" },
            recieptNo: invoiceSettings.recieptNo ?? { text: "" },
            services: invoiceSettings.services ?? [],
          });

          // Set terms and conditions
          const tcs = invoiceSettings.termsAndConditions || "";
          setTcRules(tcs ? tcs.split(/\d+\)\s?/).filter(Boolean) : [""]);

          // Update global settings if not already set
          if (!globalSettings) {
            setGlobalSettings(response.data);
          }
        } else {
          // Set default values if no data exists
          setSettings({
            displayTermsAndConditions: false,
            displayHeaderFooter: false,
            gstIncluded: false,
            notifyAdmin: false,
            isSignatureDigitalGenerated: false,
            headerText: "",
            description: "",
            address: "",
            gstNo: "",
            panNo: "",
            cinNo: "",
            header: "",
            footer: "",
            bankDetails: "",
            digitalSignature: "",
            predefinedNo: { text: "" },
            recieptNo: { text: "" },
            services: [],
          });
          setTcRules([""]);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        showToast("Failed to fetch invoice settings", "error");
        // Set defaults on error
        setSettings({
          displayTermsAndConditions: false,
          displayHeaderFooter: false,
          gstIncluded: false,
          notifyAdmin: false,
          isSignatureDigitalGenerated: false,
          headerText: "",
          description: "",
          address: "",
          gstNo: "",
          panNo: "",
          cinNo: "",
          header: "",
          footer: "",
          bankDetails: "",
          digitalSignature: "",
          predefinedNo: { text: "" },
          recieptNo: { text: "" },
          services: [],
        });
        setTcRules([""]);
      } finally {
        setFetchingData(false);
      }
    };

    fetchInvoiceSettings();
  }, [globalSettings, setGlobalSettings]);

  useEffect(() => {
    const invoiceSettings = globalSettings?.result?.[0]?.invoiceSettings;
    if (!invoiceSettings) return;

    setSettings(prev => ({
      ...prev,
      ...invoiceSettings,
      displayTermsAndConditions: invoiceSettings.displayTermsAndConditions ?? false,
      displayHeaderFooter: invoiceSettings.displayHeaderFooter ?? false,
      gstIncluded: invoiceSettings.gstIncluded ?? false,
      notifyAdmin: invoiceSettings.notifyAdmin ?? false,
      isSignatureDigitalGenerated: invoiceSettings.isSignatureDigitalGenerated ?? false,
      predefinedNo: invoiceSettings.predefinedNo ?? { text: "" },
      recieptNo: invoiceSettings.recieptNo ?? { text: "" },
      services: invoiceSettings.services ?? [],
    }));

    const tcs = invoiceSettings.termsAndConditions || "";
    setTcRules(tcs ? tcs.split(/\d+\)\s?/).filter(Boolean) : [""]);
  }, [globalSettings]);

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !Boolean(prev[field]) }));
  };

  const handleServiceChange = (field, value) => {
    setNewService((prev) => ({ ...prev, [field]: value }));
  };

  const addService = () => {
    if (newService.name) {
      setSettings((prev) => ({
        ...prev,
        services: [...(prev.services || []), { ...newService }],
      }));
      setNewService({ name: "" });
    }
  };

  const removeService = (index) => {
    setSettings((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const handleTcChange = (index, value) => {
    setTcRules((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addTcRule = () => {
    setTcRules((prev) => [...prev, ""]);
  };

  const removeTcRule = (index) => {
    setTcRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const payload = {
      description: settings.description,
      gstIncluded: settings.gstIncluded,
      footer: settings.footer,
      header: settings.header,
      predefinedNo: { text: settings.predefinedNo?.text || "" },
      recieptNo: { text: settings.recieptNo?.text || '' },
      address: settings.address,
      bankDetails: settings.bankDetails,
      cinNo: settings.cinNo,
      digitalSignature: settings.digitalSignature,
      displayBankDetails: settings.displayBankDetails,
      displayTermsAndConditions: settings.displayTermsAndConditions,
      displayHeaderFooter: settings.displayHeaderFooter,
      gstNo: settings.gstNo,
      headerText: settings.headerText,
      termsAndConditions: tcRules
        .map((rule, i) => `${i + 1}) ${rule}`)
        .join(" "),
      panNo: settings.panNo,
      isSignatureDigitalGenerated: settings.isSignatureDigitalGenerated,
      notifyAdmin: settings.notifyAdmin,
      services: (settings.services || []).map((s) => ({
        name: s.name,
      })),
    };
    setLoading(true);
    try {
      await updateInvoiceSettings(payload);
      const updated = await getAllGlobalSettingsData();
      setGlobalSettings(updated.data);
      localStorage.setItem("globalSettings", JSON.stringify(updated.data));
      showToast("Invoices updated successfully.", "success");
      setTimeout(() => {
        navigate("/home/displayboard");
      }, 300);
    } catch (err) {
      console.error(err);
      showToast("Something went wrong to update invoices!", "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <section className="inv-sett-section">
        {/* <h3 className="inv-sett-title">Invoice Settings</h3> */}
        <JusticeLoader message="Loading invoice settings..." type="gavel" />
      </section>
    );
  }

  return (
    <>
      <section className="inv-sett-section">
        {/* <h3 className="inv-sett-title">Invoice Settings</h3> */}

        <div className="settings-container">
          <div className="settings-card">
            <h4 className="card-title">Header & Footer</h4>
            <div className="toggle-row">
              <label>Display Header/Footer</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.displayHeaderFooter || false}
                  onChange={() => handleToggle("displayHeaderFooter")}
                  id="displayHeaderFooter"
                />
                <label htmlFor="displayHeaderFooter" className="toggle-slider"></label>
              </div>
            </div>

            {settings.displayHeaderFooter && (
              <div className="uploader-group">
                <FileUploader
                  label="Header Image"
                  type="header"
                  existingUrl={settings.header || ""}
                />
                <FileUploader
                  label="Footer Image"
                  type="footer"
                  existingUrl={settings.footer || ""}
                />
              </div>
            )}
          </div>

          <div className="settings-card">
            <h4 className="card-title">Invoice Details</h4>
            <div className="form-grid">
              <div className="form-group-invoice-settings">
                <label>GST Included</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.gstIncluded || false}
                    onChange={() => handleToggle("gstIncluded")}
                    id="gstIncluded"
                  />
                  <label htmlFor="gstIncluded" className="toggle-slider"></label>
                </div>
              </div>

              <div className="form-group-invoice-settings">
                <label>Header Text</label>
                <input
                  type="text"
                  value={settings.headerText || ""}
                  onChange={(e) => handleChange("headerText", e.target.value)}
                  placeholder="Enter header text"
                />
              </div>

              <div className="form-group-invoice-settings">
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={settings.predefinedNo?.text || ""}
                  onChange={(e) =>
                    handleChange("predefinedNo", {
                      ...settings.predefinedNo,
                      text: e.target.value,
                    })
                  }
                  placeholder="Enter predefined text"
                />
              </div>

              <div className="form-group-invoice-settings">
                <label>Receipt Number</label>
                <input
                  type="text"
                  value={settings.recieptNo?.text || ""}
                  onChange={(e) =>
                    handleChange("recieptNo", {
                      ...settings.recieptNo,
                      text: e.target.value,
                    })
                  }
                  placeholder="Enter receipt number format"
                />
              </div>
            </div>
          </div>

          <div className="settings-card">
            <h4 className="card-title">Company Information</h4>
            <div className="form-grid">
              <div className="form-group-invoice-settings">
                <label>Address</label>
                <input
                  type="text"
                  value={settings.address || ""}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="Company address"
                />
              </div>

              <div className="form-group-invoice-settings">
                <label>GST No.</label>
                <input
                  type="text"
                  value={settings.gstNo || ""}
                  onChange={(e) => handleChange("gstNo", e.target.value)}
                  placeholder="GST number"
                />
              </div>

              <div className="form-group-invoice-settings">
                <label>PAN No.</label>
                <input
                  type="text"
                  value={settings.panNo || ""}
                  onChange={(e) => handleChange("panNo", e.target.value)}
                  placeholder="PAN number"
                />
              </div>

              <div className="form-group-invoice-settings">
                <label>CIN No.</label>
                <input
                  type="text"
                  value={settings.cinNo || ""}
                  onChange={(e) => handleChange("cinNo", e.target.value)}
                  placeholder="CIN number"
                />
              </div>
            </div>
          </div>

          <div className="settings-card">
            <h4 className="card-title">Additional Settings</h4>
            <div className="form-group-invoice-settings">
              <label>Description</label>
              <textarea
                value={settings.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Invoice description"
                rows="3"
              />
            </div>

            <div className="toggle-row">
              <label>Notify Admin</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.notifyAdmin || false}
                  onChange={() => handleToggle("notifyAdmin")}
                  id="notifyAdmin"
                />
                <label htmlFor="notifyAdmin" className="toggle-slider"></label>
              </div>
            </div>

            <div className="toggle-row">
              <label>Display Bank Details</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.displayBankDetails || false}
                  onChange={() => handleToggle("displayBankDetails")}
                  id="displayBankDetails"
                />
                <label htmlFor="displayBankDetails" className="toggle-slider"></label>
              </div>
            </div>

            {settings.displayBankDetails && (
              <div className="form-group-invoice-settings">
                <label>Bank Details</label>
                <textarea
                  value={settings.bankDetails || ""}
                  onChange={(e) => handleChange("bankDetails", e.target.value)}
                  placeholder="Bank account details"
                  rows="3"
                />
              </div>
            )}
          </div>

          <div className="settings-card">
            <h4 className="card-title">Terms & Conditions</h4>
            <div className="toggle-row">
              <label>Display Terms & Conditions</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.displayTermsAndConditions || false}
                  onChange={() => handleToggle("displayTermsAndConditions")}
                  id="displayTermsAndConditions"
                />
                <label htmlFor="displayTermsAndConditions" className="toggle-slider"></label>
              </div>
            </div>

            {settings.displayTermsAndConditions && (
              <div className="tc-container">
                <label>Terms & Conditions Rules</label>
                {tcRules.map((rule, index) => (
                  <div key={index} className="tc-rule-row">
                    <span className="rule-number">{index + 1}.</span>
                    <input
                      type="text"
                      value={rule}
                      onChange={(e) => handleTcChange(index, e.target.value)}
                      placeholder={`Rule ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeTcRule(index)}
                      className="remove-rule-btn"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addTcRule} className="add-rule-btn">
                  + Add Rule
                </button>
              </div>
            )}
          </div>

          <div className="settings-card">
            <h4 className="card-title">Digital Signature</h4>
            <div className="toggle-row">
              <label>Digital Signature Enabled</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.isSignatureDigitalGenerated || false}
                  onChange={() => handleToggle("isSignatureDigitalGenerated")}
                  id="isSignatureDigitalGenerated"
                />
                <label htmlFor="isSignatureDigitalGenerated" className="toggle-slider"></label>
              </div>
            </div>

            {settings.isSignatureDigitalGenerated && (
              <FileUploader
                label="Digital Signature"
                type="signature"
                existingUrl={settings.digitalSignature || ""}
              />
            )}
          </div>

          <div className="settings-card">
            <h4 className="card-title">Services</h4>
            <div className="form-group-invoice-settings">
              <label>Add New Service</label>
              <div className="service-input-row">
                <input
                  placeholder="Service name"
                  value={newService.name}
                  onChange={(e) => handleServiceChange("name", e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addService(); }}
                />
                <button type="button" onClick={addService} className="add-service-btn">
                  Add
                </button>
              </div>
            </div>

            <div className="services-list">
              {settings.services?.map((service, i) => (
                <div key={i} className="service-item">
                  <span className="service-name">
                    {i + 1}. {service.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeService(i)}
                    className="remove-service-btn"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="cancel-btn"
            onClick={() => navigate("/home/displayboard")}
          >
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </section>
    </>
  );
};

export default InvoiceSettings;